/**
 * Working out how little has to be re-rendered.
 *
 * Synthesis is nearly all of the cost of a render, so the difference between
 * re-doing five layers and re-doing one is the difference between waiting four
 * seconds and waiting under one. That gap is what decides whether someone
 * experiments freely or makes three changes and stops.
 *
 * Kept as a pure function over two pieces rather than as flags set by whichever
 * control was touched. Flags rot: a new control gets added, nobody sets its
 * flag, and the editor quietly stops re-rendering something. Comparing the two
 * pieces cannot go out of date, and the worst it can do is re-render too much.
 */

import type { LayerRole, Piece } from "./piece.js";

const ROLES: LayerRole[] = ["pad", "bass", "rhythm", "melody", "perc"];

/**
 * Anything that changes the notes or the grid every layer sits on.
 *
 * `name` is deliberately absent: renaming a piece changes no audio, and
 * re-rendering ninety seconds because someone typed a letter is exactly the
 * kind of wait that makes a tool feel heavy.
 */
const STRUCTURAL = [
  "key", "tempo", "progression", "duration", "barsPerChord", "seed", "loop",
] as const;

/**
 * Fields that change the sound without changing a single note.
 *
 * Space and fades are applied after synthesis, so moving them needs the mix
 * redone and nothing re-synthesised. Treating them as structural would throw
 * away five layers of work to change a reverb tail.
 */
const FINISH_ONLY = ["space", "fadeIn", "fadeOut"] as const;

/** `finish` means remix what is already synthesised; `[]` means do nothing. */
export type Changed = "everything" | "finish" | LayerRole[];

export function changedLayers(before: Piece | null, after: Piece): Changed {
  if (!before) return "everything";

  for (const field of STRUCTURAL) {
    if (before[field] !== after[field]) return "everything";
  }

  const changed: LayerRole[] = [];
  for (const role of ROLES) {
    // Compared as JSON rather than field by field, so a setting added to
    // LayerSpec later is covered without anyone remembering to come back here.
    if (JSON.stringify(before.layers[role]) !== JSON.stringify(after.layers[role])) {
      changed.push(role);
    }
  }
  if (changed.length) return changed;

  for (const field of FINISH_ONLY) {
    if (before[field] !== after[field]) return "finish";
  }
  return [];
}

/** Whether a change needs any audio work at all. Renaming does not. */
export function needsRender(changed: Changed): boolean {
  return changed === "everything" || changed === "finish" || changed.length > 0;
}
